import { useLoader } from "../../../components/loader";
import { GUJARAT_GST_CODE } from "../../../constant/commonConst";
import { fetcher } from "../../../helper/api";
import logoYellow from "../../../assets/images/mail_logo.jpg";
import { generateRows } from "./headerColumns";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import "../../../assets/fonts/Bahnschrift-normal";
import "../../../assets/fonts/Bahnschrift-bold";
import "../../../assets/fonts/Gotham-bold";
import { downloadPDF } from "../../../constant/icons";
import { Button, UncontrolledTooltip } from "reactstrap";
import { useState } from "react";
import moment from "moment";
import { findStateAndStateCode } from "../common";
import "../../../assets/fonts/style.css";
import { PDF_CONSTANTS } from "../../businessProposal/pdfGenerate/pdfConstants";

export default function GeneratePdf(props: any) {
  const { rowData } = props;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTooltip = () => {
    setTooltipOpen(!tooltipOpen);
  };
  const loading = useLoader();

  const doc: any = new jsPDF();
  const constants = PDF_CONSTANTS(doc);
  const normalizeColumnName = (col: any) =>
    col.replace(/\s+/g, "").toLowerCase();
  let y = 0;
  let isLastPage = false;

  const addHeader = (bookingData: any, getStateAndCode: any): number => {
    const { pageWidth, margins, colors, fonts, layout } = constants;
    const { left: leftMargin, right: rightMargin } = margins;
    const { logo, title, proposalSheet: bookingSheet, sections } = layout;
    const maxWidth = pageWidth - sections.rightX - rightMargin;

    // Fixed top content (logo and right-aligned titles)
    doc.addImage(logoYellow, "PNG", logo.x, logo.y, logo.width, logo.height);
    doc.setFontSize(fonts.sizes.subtitle);
    try {
      doc.setFont(fonts.primary, "bold");
    } catch (e) {
      doc.setFont(fonts.fallback, "bold");
    }
    doc.text(
      "ADVERTISING SERVICES",
      pageWidth - rightMargin,
      title.advertisingServicesY,
      { align: "right" }
    );
    doc.setFontSize(fonts.sizes.small);
    doc.setFont(fonts.primary, "normal");
    doc.text("BOOKING SHEET", pageWidth - rightMargin, title.proposalSheetY, {
      align: "right",
    });

    // Booking Sheet details
    doc.setFontSize(fonts.sizes.title);
    doc.setTextColor(...colors.yellow);
    doc.text("BOOKING SHEET", bookingSheet.x, bookingSheet.titleY);
    doc.setFontSize(fonts.sizes.normal);
    doc.setTextColor(...colors.gray);
    doc.text(
      `Booking ID: ${bookingData?.bookingNo || ""}`,
      bookingSheet.x,
      bookingSheet.details.proposalIdY
    );
    doc.text(
      `Booking Date: ${moment(bookingData.createDate).format("DD/MM/YYYY")}`,
      bookingSheet.x,
      bookingSheet.details.proposalDateY
    );
    doc.text(
      `Booking By: ${bookingData?.user?.name || ""}`,
      bookingSheet.x,
      bookingSheet.details.proposalByY
    );

    // Left section: Booking From
    doc.setFontSize(fonts.sizes.title);
    doc.setTextColor(...colors.yellow);
    doc.text("Booking From", sections.leftX, sections.startY);

    doc.setFont(fonts.fallback, "bold");
    doc.setFontSize(fonts.sizes.normal);
    doc.setTextColor(...colors.black);
    doc.text(
      "CRAFT MEDIA",
      sections.leftX,
      sections.startY + sections.nameOffsetY
    );

    doc.setFont(fonts.primary, "normal");
    doc.setTextColor(...colors.gray);
    doc.setFontSize(fonts.sizes.small);
    const leftAddressStartY = sections.startY + sections.addressStartOffsetY;
    const leftAddressLines = [
      "A-409, The Landmark, URJANAGAR-I",
      "Kudasan, 382421, Gandhinagar - GUJ (IND)",
      "GSTIN / UNI: 24BVWPM5351E1ZB",
      "STATE NAME: GUJARAT",
      "PAN: 24BVWPM5351E",
      "CONTACT NO: +91 9586622346, +91 9586522346",
    ];
    leftAddressLines.forEach((line, idx) => {
      doc.text(
        line,
        sections.leftX,
        leftAddressStartY + idx * sections.leftAddressLineHeight
      );
    });
    const leftSectionLastY =
      leftAddressStartY +
      (leftAddressLines.length - 1) * sections.leftAddressLineHeight;

    // Right section: Booking For
    doc.setFontSize(fonts.sizes.title);
    doc.setTextColor(...colors.yellow);
    doc.text("Booking For", sections.rightX, sections.startY);

    doc.setFont(fonts.fallback, "bold");
    doc.setFontSize(fonts.sizes.normal);
    doc.setTextColor(...colors.black);
    doc.text(
      `${bookingData.client?.businessName
        ? bookingData.client?.businessName
        : bookingData.client?.contactPerson || ""
      }`,
      sections.rightX,
      sections.startY + sections.nameOffsetY
    );

    doc.setFont(fonts.primary, "normal");
    doc.setTextColor(...colors.gray);
    doc.setFontSize(fonts.sizes.small);
    const rightAddressStartY = sections.startY + sections.addressStartOffsetY;
    const address = `ADDRESS: ${bookingData.client?.billingAddress || ""}`;
    const wrappedAddress = doc.splitTextToSize(address, maxWidth);

    let addressY = rightAddressStartY;
    wrappedAddress.forEach((line: string) => {
      doc.text(line, sections.rightX, addressY);
      addressY += sections.rightAddressLineHeight;
    });
    const fieldsStartY = addressY;

    const fields = [
      `GSTIN / INI: ${bookingData.client?.GSTIN || ""}`,
      "PLACE OF SUPPLY:",
      `STATE NAME: ${getStateAndCode.state}`,
      `STATE CODE: ${getStateAndCode.stateCode}`,
      `PAN: ${bookingData.client?.PANNo || ""}`,
      `CONTACT NO: ${bookingData.client?.contactPersonMobileNo || ""}`,
    ];
    let fieldY = fieldsStartY;
    fields.forEach((field: string) => {
      doc.text(field, sections.rightX, fieldY);
      fieldY += sections.fieldLineHeight;
    });
    const rightSectionLastY =
      fieldsStartY + (fields.length - 1) * sections.fieldLineHeight;

    return Math.max(leftSectionLastY, rightSectionLastY);
  };

  const addTableHeader = (tableStartY: number) => {
    const { colors, fonts, layout, margins, pageWidth } = constants;
    const { table } = layout;
    const tableWidth = pageWidth - margins.left - margins.right;
    doc.setFontSize(fonts.sizes.subtitle);
    doc.setFont(fonts.fallback, "bold");
    doc.setTextColor(...colors.black);
    doc.text("SUMMARY OF SERVICES CHARGES", table.summaryX, tableStartY - 4);
    const amountX = margins.left + tableWidth - doc.getTextWidth("AMOUNT") - 7;
    doc.text("AMOUNT", amountX, tableStartY - 4);
    y = tableStartY + 0; // Increase y after header to provide space for section titles
  };

  // const addFooter = (proposalData: any, startY: number) => {
  //   const { pageWidth, pageHeight, margins, colors, fonts, layout } = constants;
  //   const { terms, contact, totals } = layout.footer;
  //   const rightX = layout.sections.rightX;

  //   // GST Calculations
  //   const SubTotalAmount = proposalData.mediaInventory.reduce(
  //     (a: number, i: any) => a + (i.amount || 0),
  //     0
  //   );
  //   const igst = SubTotalAmount * 0.18;
  //   const cgst = SubTotalAmount * 0.09;
  //   const sgst = SubTotalAmount * 0.09;

  //   const isGujarat =
  //     proposalData?.client?.GSTIN?.slice(-2) === GUJARAT_GST_CODE;

  //   //-------------------------------------------------------
  //   // 📍 Determine the aligned starting Y for BOTH columns
  //   //-------------------------------------------------------
  //   let yLeft = startY + 20;
  //   let yRight = startY + 20;

  //   //-------------------------------------------------------
  //   // 🧾 Print RIGHT COLUMN TOTALS (Top aligned)
  //   //-------------------------------------------------------
  //   doc.setFontSize(fonts.sizes.normal);
  //   doc.setFont(fonts.fallback, "bold");
  //   doc.setTextColor(...colors.black);

  //   const rightLine = (txt: string) => {
  //     doc.text(txt, rightX, yRight);
  //     yRight += totals.lineHeight;
  //   };

  //   rightLine(`SUB TOTAL IN INR: ${SubTotalAmount.toFixed(2)}`);

  //   if (isGujarat) {
  //     rightLine(`IGST @ 18%: ${igst.toFixed(2)}`);
  //   }
  //   rightLine(`CGST @ 9%: ${cgst.toFixed(2)}`);
  //   rightLine(`SGST @ 9%: ${sgst.toFixed(2)}`);

  //   yRight += totals.spaceBeforeTotal;

  //   doc.setFontSize(fonts.sizes.large);
  //   doc.setFont(fonts.primary, "bold");
  //   doc.setTextColor(...colors.gray);
  //   doc.text(
  //     `Total (INR): ${(SubTotalAmount + (isGujarat ? igst : cgst + sgst)).toFixed(2)}`,
  //     rightX,
  //     yRight
  //   );

  //   yRight += totals.officeStartOffsetY;
  //   doc.setTextColor(...colors.black);
  //   doc.setFontSize(fonts.sizes.small);
  //   doc.setFont(fonts.primary, "normal");

  //   [
  //     "Reg Office. 86, Shakti Complex, Opp Krishna Hospital,",
  //     "Mota Chiloda, NH-8, 382 355 Gandhinagar - Guj (IND)",
  //     "",
  //     "Corp Office, A-409, The Landmark, Urjanagar 1",
  //     "Kudasan, 382 421 Gandhinagar - Guj (IND)"
  //   ].forEach(line => {
  //     if (line === "") {
  //       doc.setDrawColor(255, 193, 7);
  //       doc.setLineWidth(0.5);
  //       doc.line(rightX, yRight, rightX + 20, yRight);
  //       yRight += 4;
  //     } else {
  //       doc.text(line, rightX, yRight);
  //       yRight += totals.officeLineHeight;
  //     }
  //   });

  //   //-------------------------------------------------------
  //   // 📜 LEFT COLUMN — TERMS & CONDITIONS
  //   //-------------------------------------------------------
  //   doc.setFontSize(fonts.sizes.normal);
  //   doc.setFont(fonts.fallback, "bold");
  //   doc.text("TERMS & CONDITIONS", margins.left, yLeft);

  //   yLeft += terms.titleOffsetY;

  //   doc.setFont(fonts.primary, "normal");
  //   doc.setFontSize(fonts.sizes.tiny);

  //   const termText = proposalData?.term?.description || "";
  //   const list = termText.split("\n").map((t: any) => t.trim()).filter(Boolean);
  //   const width = pageWidth * 0.5 - margins.left;

  //   const newPage = () => {
  //     doc.addPage();
  //     yLeft = margins.top;
  //     yRight = margins.top; // Maintain ✔ alignment on new page
  //   };

  //   list.forEach((term: any) => {
  //     const lines = doc.splitTextToSize(term, width);
  //     lines.forEach((l: any) => {
  //       if (yLeft > pageHeight - margins.bottom) newPage();
  //       doc.text(l, margins.left, yLeft);
  //       yLeft += terms.lineHeight;
  //     });
  //   });

  //   //-------------------------------------------------------
  //   // 📞 Contact Info (follows Terms)
  //   //-------------------------------------------------------
  //   yLeft += terms.spacingBeforeContact;
  //   if (yLeft > pageHeight - margins.bottom) newPage();

  //   doc.setFontSize(fonts.sizes.normal);
  //   doc.setFont(fonts.fallback, "bold");
  //   doc.text("Contact Us", margins.left, yLeft);

  //   yLeft += contact.lineHeight;
  //   doc.setFont(fonts.primary, "normal");
  //   doc.setFontSize(fonts.sizes.small);

  //   [
  //     "Call - 95866 22346, 9586522346",
  //     "E mail - info.craftmedia@gmail.com",
  //     "Web - www.craftmedia.in"
  //   ].forEach(line => {
  //     if (yLeft > pageHeight - margins.bottom) newPage();
  //     doc.text(line, margins.left, yLeft);
  //     yLeft += contact.lineHeight;
  //   });
  // };

  const addFooter = (proposalData: any, startY: number) => {
    const { pageWidth, pageHeight, margins, colors, fonts, layout } = constants;
    const { terms, contact, totals } = layout.footer;
    const rightX = layout.sections.rightX;

    // GST Calculations
    const SubTotalAmount = proposalData.mediaInventory.reduce(
      (acc: number, item: any) => acc + (item.amount || 0),
      0
    );
    const igst = SubTotalAmount * 0.18;
    const cgst = SubTotalAmount * 0.09;
    const sgst = SubTotalAmount * 0.09;

    const isGujarat = proposalData?.client?.GSTIN?.slice(-2) === GUJARAT_GST_CODE;

    const availableHeight = pageHeight - startY - margins.bottom;
    console.log("availableHeight", availableHeight)
    // Force page break if footer goes too low
    if (availableHeight < 25) {
      doc.addPage();
      startY = margins.top;
    }

    //-------------------------------------------------------
    // RIGHT COLUMN TOTALS
    //-------------------------------------------------------
    let yRight = startY;

    const writeRight = (text: string) => {
      doc.setFontSize(fonts.sizes.normal);
      doc.setFont(fonts.fallback, "bold");
      doc.setTextColor(...colors.black);
      doc.text(text, rightX, yRight);
      yRight += totals.lineHeight;
    };

    writeRight(`SUB TOTAL IN INR: ${SubTotalAmount.toFixed(2)}`);
    if (isGujarat) writeRight(`IGST @ 18%: ${igst.toFixed(2)}`);
    writeRight(`CGST @ 9%: ${cgst.toFixed(2)}`);
    writeRight(`SGST @ 9%: ${sgst.toFixed(2)}`);
    yRight += totals.spaceBeforeTotal;

    // Total text baseline
    const totalTextX = rightX;
    const totalTextY = yRight;

    doc.setFontSize(fonts.sizes.large);
    doc.setFont(fonts.primary, "bold");
    doc.setTextColor(...colors.gray);
    doc.text(
      `Total (INR): ${(SubTotalAmount + (isGujarat ? igst : cgst + sgst)).toFixed(2)}`,
      totalTextX,
      totalTextY
    );

    // 🔽 Thick black divider above Total
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1.0);
    doc.line(
      totalTextX,
      totalTextY - totals.totalLineOffset,
      totalTextX + totals.totalLineWidth,
      totalTextY - totals.totalLineOffset
    );

    // 🔽 Thin gray line under Total
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.6);
    doc.line(
      totalTextX,
      totalTextY + 3,
      totalTextX + totals.totalLineWidth,
      totalTextY + 3
    );

    // Move pointer below both lines
    yRight = totalTextY + totals.officeStartOffsetY;
    doc.setTextColor(...colors.black);
    doc.setFont(fonts.primary, "normal");
    doc.setFontSize(fonts.sizes.small);

    // Office details area
    [
      "Reg Office. 86, Shakti Complex, Opp Krishna Hospital,",
      "Mota Chiloda, NH-8, 382 355 Gandhinagar - Guj (IND)",
      "",
      "Corp Office, A-409, The Landmark, Urjanagar 1",
      "Kudasan, 382 421 Gandhinagar - Guj (IND)",
    ].forEach((line) => {
      if (line === "") {
        doc.setDrawColor(255, 193, 7);
        doc.setLineWidth(0.8);
        doc.line(rightX, yRight, rightX + 20, yRight);
        yRight += 6;
      } else {
        doc.text(line, rightX, yRight);
        yRight += totals.officeLineHeight;
      }
    });

    //-------------------------------------------------------
    // LEFT TERMS COLUMN
    //-------------------------------------------------------
    let yLeft = startY;

    const newPageLeft = () => {
      doc.addPage();
      yLeft = margins.top;
    };

    doc.setFontSize(fonts.sizes.normal);
    doc.setFont(fonts.fallback, "bold");
    doc.setTextColor(...colors.black);
    doc.text("TERMS & CONDITIONS", margins.left, yLeft);

    yLeft += terms.titleOffsetY;
    doc.setFontSize(fonts.sizes.tiny);
    doc.setFont(fonts.primary, "normal");

    const termList = (proposalData?.term?.description || "")
      .split("\n")
      .map((t: string) => t.trim())
      .filter(Boolean);

    const maxWidth = pageWidth * 0.5 - margins.left;

    termList.forEach((term: any) => {
      const lines = doc.splitTextToSize(term, maxWidth);
      lines.forEach((textLine: string) => {
        if (yLeft > pageHeight - margins.bottom) newPageLeft();
        doc.text(textLine, margins.left, yLeft);
        yLeft += terms.lineHeight;
      });
    });

    // Contact info
    yLeft += terms.spacingBeforeContact;
    if (yLeft > pageHeight - margins.bottom) newPageLeft();

    doc.setFontSize(fonts.sizes.normal);
    doc.setFont(fonts.fallback, "bold");
    doc.text("Contact Us", margins.left, yLeft);

    yLeft += contact.lineHeight;
    doc.setFont(fonts.primary, "normal");
    doc.setFontSize(fonts.sizes.small);

    ["Call - 95866 22346, 9586522346", "E mail - info.craftmedia@gmail.com", "Web - www.craftmedia.in"]
      .forEach((line) => {
        if (yLeft > pageHeight - margins.bottom) newPageLeft();
        doc.text(line, margins.left, yLeft);
        yLeft += contact.lineHeight;
      });
  };


  const printPDF = async () => {
    const { pageWidth, margins, colors, header, border, layout } = constants;
    const bookingData: any = await fetcher(
      "/booking",
      { id: rowData.id },
      loading
    );
    const getStateAndCode: any = await findStateAndStateCode(
      bookingData.client?.GSTIN
    );

    const headerEndY = addHeader(bookingData, getStateAndCode);
    const tableStartY = headerEndY + header.spaceAfter;

    doc.setFillColor(...colors.yellow);
    doc.roundedRect(
      margins.left,
      tableStartY - header.height,
      pageWidth - margins.left - margins.right,
      header.height,
      border.radius,
      border.radius,
      "F"
    );

    addTableHeader(tableStartY);

    let isFirstSection = true;
    let finalTableY = y; // Initialize with current Y position after header

    const addSectionTitle = (title: string) => {
      const { fonts, layout } = constants;
      const firstPadding = 5;
      const normalPadding = 8;
      const spacingFactor = 0.5;
      const additionalOffset = 0;
      const sectionIndent = 3;
      doc.setFontSize(fonts.sizes.subtitle);
      doc.setFont(fonts.fallback, "bold");
      y += isFirstSection ? firstPadding : normalPadding;
      doc.text(title, margins.left + sectionIndent, y);
      const textHeight = doc.getTextDimensions(title).h;
      y += textHeight * spacingFactor + additionalOffset;
      isFirstSection = false;
    };

    const renderTable = (section: any) => {
      const columns = section.columns.map((col: any) => ({
        title: col,
        dataKey: normalizeColumnName(col),
      }));

      const rows = section.rows.map((row: any) => {
        let rowData: any = {};
        section.columns.forEach((col: any) => {
          const normalizedCol = normalizeColumnName(col);
          rowData[normalizedCol] = row[normalizedCol] || "N/A";
        });
        return rowData;
      });

      addSectionTitle(section.title);

      doc.autoTable({
        startY: y,
        body: rows,
        columns: columns,
        theme: "striped",
        margin: {
          top: 0,
          bottom: margins.bottom + 3,
          left: margins.left + 3,
          right: margins.right + 3,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: constants.colors.tableHeaderText,
          fontSize: constants.fonts.sizes.tablehead,
          fontStyle: "bold",
          lineColor: [85, 0, 0, 0],
        },
        bodyStyles: {
          textColor: [...constants.colors.gray],
          fontSize: constants.fonts.sizes.tablehead,
        },
        columnStyles: {
          size: { halign: "center" },
          sqft: { halign: "center" },
          qty: { halign: "center" },
          ratepermonth: { halign: "center" },
          ratepersqft: { halign: "center" },
          amount: { halign: "right" },
        },
        didParseCell: (data: any) => {
          if (data.section === "head") {
            if (["size", "sqft", "qty"].includes(data.column.dataKey)) {
              data.cell.styles.halign = "center";
            }
            if (
              ["ratepermonth", "ratepersqft", "amount"].includes(
                data.column.dataKey
              )
            ) {
              data.cell.styles.halign = "right";
            }
          }
        },
        styles: {
          cellPadding: layout.table.cellPadding,
          overflow: "linebreak",
        },

        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            doc.setFillColor(...colors.yellow);
            doc.roundedRect(
              margins.left,
              margins.top - header.height,
              pageWidth - margins.left - margins.right,
              header.height,
              border.radius,
              border.radius,
              "F"
            );
            doc.setDrawColor(...colors.tableHeaderText);
            doc.setFontSize(constants.fonts.sizes.small);
            doc.setFont(constants.fonts.fallback, "bold");
            doc.setTextColor(...constants.colors.black);
            doc.text(
              section.title,
              layout.table.newPageTitleX,
              layout.table.newPageTitleY
            );
          }
        },
        didDrawCell: (data: any) => {
          if (data.section === "head") {
            const { x, y: cellY, width, height } = data.cell;
            doc.setDrawColor(55, 55, 55);
            doc.setLineWidth(layout.footer.lineWidth);
            doc.line(x, cellY, x + width, cellY); // Top border
            doc.line(x, cellY + height, x + width, cellY + height); // Bottom border
          }
        },
      });

      y = doc.lastAutoTable.finalY;
      finalTableY = y; // Update the final Y position after each table
    };

    const headerColumns = [
      {
        title: "SALE OF SPACE FOR OUTDOOR ADVERTISEMENT HOARDING",
        columns: [
          "MEDIA ID",
          "CITY",
          "LOCATION",
          "SIZE",
          "SQ FT",
          "QTY",
          "RATE PER MONTH",
          "ST DATE",
          "END DATE",
          "AMOUNT",
        ],
        rows:
          bookingData.mediaInventory.filter(
            (item: any) => item.groupCode === "SOAH"
          ).length > 0
            ? generateRows(
              bookingData.mediaInventory.filter(
                (item: any) => item.groupCode === "SOAH"
              ),
              "mediaItemId"
            )
            : null,
      },
      {
        title: "PURCHASE OF SPACE FOR OUTDOOR ADVERTISEMENT HOARDING",
        columns: [
          "AGENCY ID",
          "CITY",
          "LOCATION",
          "SIZE",
          "SQ FT",
          "QTY",
          "RATE PER MONTH",
          "ST DATE",
          "END DATE",
          "AMOUNT",
        ],
        rows:
          bookingData.mediaInventory.filter(
            (item: any) => item.groupCode === "POAH"
          ).length > 0
            ? generateRows(
              bookingData.mediaInventory.filter(
                (item: any) => item.groupCode === "POAH"
              ),
              "agencyId"
            )
            : null,
      },
      {
        title: "FLEX BANNER PRINTING HOARDING",
        columns: [
          "MEDIA ID",
          "CITY",
          "LOCATION",
          "SIZE",
          "SQ FT",
          "QTY",
          "RATE PER SQ FT",
          "MATERIAL",
          "AMOUNT",
        ],
        rows:
          bookingData.mediaInventory.filter(
            (item: any) => item.groupCode === "FBPH"
          ).length > 0
            ? generateRows(
              bookingData.mediaInventory.filter(
                (item: any) => item.groupCode === "FBPH"
              ),
              "agencyId"
            )
            : null,
      },
      {
        title: "FLEX BANNER MOUNTING",
        columns: [
          "MEDIA ID",
          "CITY",
          "LOCATION",
          "SIZE",
          "SQ FT",
          "QTY",
          "RATE PER SQ FT",
          "AMOUNT",
        ],
        rows:
          bookingData.mediaInventory.filter(
            (item: any) => item.groupCode === "FBM"
          ).length > 0
            ? generateRows(
              bookingData.mediaInventory.filter(
                (item: any) => item.groupCode === "FBM"
              ),
              "agencyId"
            )
            : null,
      },
      {
        title: "SALE OF SPACE FOR OUTDOOR ADVERTISEMENT POLE KIOSK",
        columns: [
          "ROUTE ID",
          "CITY",
          "LOCATION",
          "SIZE",
          "SQ FT",
          "QTY",
          "RATE PER MONTH",
          "ST DATE",
          "END DATE",
          "AMOUNT",
        ],
        rows:
          bookingData.mediaInventory.filter(
            (item: any) => item.groupCode === "SOAPK"
          ).length > 0
            ? generateRows(
              bookingData.mediaInventory.filter(
                (item: any) => item.groupCode === "SOAPK"
              ),
              "routeId"
            )
            : null,
      },
      {
        title: "FLEX BANNER PRINTING AND MOUNTING POLE KIOSK",
        columns: [
          "MEDIA ID",
          "ZONE",
          "MEDIA TYPE",
          "SIZE",
          "SERIES",
          "QTY",
          "RATE PER POLE KIOSK",
          "MATERIAL",
          "AMOUNT",
        ],
        rows:
          bookingData.mediaInventory.filter(
            (item: any) => item.groupCode === "FPMPK"
          ).length > 0
            ? generateRows(
              bookingData.mediaInventory.filter(
                (item: any) => item.groupCode === "FPMPK"
              ),
              "mediaItemId"
            )
            : null,
      },
    ].filter((section) => section.rows !== null);

    headerColumns.forEach((section) => {
      renderTable(section);
    });

    // Draw main border around all table sections - only if we have valid dimensions
    const totalTableHeight = finalTableY - (tableStartY - header.height);

    // Only draw border if we have valid positive dimensions
    if (totalTableHeight > 0 && finalTableY > tableStartY) {
      doc.setDrawColor(180, 180, 180); // light gray
      doc.setLineWidth(layout.footer.lineWidth);

      doc.roundedRect(
        margins.left,
        tableStartY - header.height,
        pageWidth - margins.left - margins.right,
        totalTableHeight,
        border.radius,
        border.radius,
        "S"
      );
    }

    // Add footer immediately after the table ends
    const footerStartY = finalTableY + 7;
    addFooter(bookingData, footerStartY);


    doc.save(`${rowData.bookingNo}.pdf`);
  };

  return (
    <div>
      <Button
        color="link"
        className="p-0 text-danger"
        onClick={printPDF}
        id="downloadTooltip"
      >
        {downloadPDF.icon}
      </Button>
      <UncontrolledTooltip
        isOpen={tooltipOpen}
        target="downloadTooltip"
        toggle={toggleTooltip}
      >
        DownloadPDF
      </UncontrolledTooltip>
    </div>
  );
}
